import React from 'react'
import { Well } from '@pushly/aqe/lib/components'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Input, Select } from 'antd'
import { RecurrenceSubscriberFrequencyCap, SubscriberFrequencyCapWindow } from '../../../../models/campaign/recurrence-subscriber-frequency-cap'

export interface ISubscriberEntryCapSectionProps {
  disabled: boolean
  cap: RecurrenceSubscriberFrequencyCap | undefined
  onChange: (next: RecurrenceSubscriberFrequencyCap | undefined) => void
}

const SubscriberEntryCapSectionCmp: React.FC<ISubscriberEntryCapSectionProps> = ({
  disabled,
  cap,
  onChange,
}) => {
  const entryCapType: 'no-fcap' | 'once' | 'once_per_criteria' = (cap?.getType?.() as any) || 'no-fcap'
  const showOncePerCriteria = entryCapType === 'once_per_criteria'
  const entryCapFrequency = cap?.getFrequency?.()
  const entryCapWindow = cap?.getWindow?.()

  const handleTypeChange = (value: 'no-fcap' | 'once' | 'once_per_criteria') => {
    if (disabled) return
    if (value === 'no-fcap') {
      onChange(undefined)
      return
    }

    let nextCap: RecurrenceSubscriberFrequencyCap = cap?.clone?.() ?? RecurrenceSubscriberFrequencyCap.build({ type: value })
    nextCap.setType(value as any)

    if (value === 'once_per_criteria' && !cap) {
      nextCap.setFrequency(1)
      nextCap.setWindow('days')
    }

    onChange(nextCap)
  }

  const handleFreqChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    const v = e?.target?.value
    const parsed = v === '' ? undefined : Number.parseInt(v, 10)
    let nextCap: RecurrenceSubscriberFrequencyCap = cap?.clone?.() ?? RecurrenceSubscriberFrequencyCap.build({ type: 'once_per_criteria' })
    // Ensure type remains once_per_criteria when editing frequency
    nextCap.setType('once_per_criteria')
    nextCap.setFrequency(Number.isNaN(parsed as any) ? undefined : (parsed as number | undefined))
    if (!nextCap.getWindow?.()) {
      nextCap.setWindow('days')
    }
    onChange(nextCap)
  }

  const handleWindowChange = (value: SubscriberFrequencyCapWindow) => {
    if (disabled) return
    let nextCap: RecurrenceSubscriberFrequencyCap = cap?.clone?.() ?? RecurrenceSubscriberFrequencyCap.build({ type: 'once_per_criteria' })
    nextCap.setType('once_per_criteria')
    nextCap.setWindow(value)
    if (!nextCap.getFrequency?.()) {
      nextCap.setFrequency(1)
    }
    onChange(nextCap)
  }

  return (
    <Well mode="ghost" title="Entry Frequency Cap" hideFooter={true} className="entry-cap-well">
      <Form.Item className="campaign-trigger-editor-label" label="Subscribers not currently in this campaign">
        <Select
          className="campaign-trigger-editor-rec-frequency-select"
          size="middle"
          disabled={disabled}
          value={entryCapType}
          onChange={handleTypeChange}
        >
          <Select.Option value="no-fcap">may enter this campaign every time it runs</Select.Option>
          <Select.Option value="once">may only enter this campaign once in their lifetime</Select.Option>
          <Select.Option value="once_per_criteria">may only enter this campaign once every</Select.Option>
        </Select>
        {showOncePerCriteria && (
          <div className="rcte-fcap-criteria-builder">
            <div className="rcte-fcap-frequency-wrapper">
              <Input
                className="rcte-fcap-frequency"
                size="small"
                disabled={disabled}
                defaultValue={entryCapFrequency}
                value={entryCapFrequency}
                onChange={handleFreqChange}
                addonAfter={
                  <Select
                    className="rcte-fcap-window-select"
                    disabled={disabled}
                    defaultValue={entryCapWindow}
                    value={entryCapWindow}
                    onChange={handleWindowChange}
                  >
                    <Select.Option value="days">day(s)</Select.Option>
                    <Select.Option value="months">month(s)</Select.Option>
                  </Select>
                }
              />
            </div>
          </div>
        )}
      </Form.Item>

    </Well>
  )
}

export default React.memo(SubscriberEntryCapSectionCmp)
